'use client';

import { useCallback } from 'react';

import type { z } from 'zod';

import { useSignInWithEmailPassword } from '@kit/supabase/hooks/use-sign-in-with-email-password';

import { useCaptcha } from '../captcha/client';
import { useLastAuthMethod } from '../hooks/use-last-auth-method';
import type { PasswordSignInSchema } from '../schemas/password-sign-in.schema';
import { AuthErrorAlert } from './auth-error-alert';
import { PasswordSignInForm } from './password-sign-in-form';
import {
  type SsoDomainGuardConfig,
  useSsoDomainGuard,
} from './sso-domain-guard';

export function PasswordSignInContainer({
  onSignIn,
  captchaSiteKey,
  ssoGuard: ssoGuardConfig,
}: {
  onSignIn?: (userId?: string) => unknown;
  captchaSiteKey?: string;
  ssoGuard: SsoDomainGuardConfig;
}) {
  const captcha = useCaptcha({ siteKey: captchaSiteKey });
  const signInMutation = useSignInWithEmailPassword();
  const { recordAuthMethod } = useLastAuthMethod();
  const ssoGuard = useSsoDomainGuard(ssoGuardConfig);
  const isLoading = signInMutation.isPending || ssoGuard.isChecking;
  const isRedirecting = signInMutation.isSuccess;
  const captchaLoading = !captcha.isReady;

  const onSubmit = useCallback(
    async (credentials: z.output<typeof PasswordSignInSchema>) => {
      if (!(await ssoGuard.canUseEmail(credentials.email))) {
        signInMutation.reset();
        return;
      }

      try {
        const data = await signInMutation.mutateAsync({
          ...credentials,
          options: { captchaToken: captcha.token },
        });

        // Record successful password sign-in
        recordAuthMethod('password', { email: credentials.email });

        if (onSignIn) {
          const userId = data?.user?.id;

          onSignIn(userId);
        }
      } catch {
        // wrong credentials, do nothing
      } finally {
        captcha.reset();
      }
    },
    [captcha, onSignIn, signInMutation, recordAuthMethod, ssoGuard],
  );

  return (
    <>
      <AuthErrorAlert error={signInMutation.error} />

      {ssoGuard.notice}

      <div onInput={ssoGuard.onEmailInput}>
        <PasswordSignInForm
          onSubmit={onSubmit}
          loading={isLoading}
          redirecting={isRedirecting}
          captchaLoading={captchaLoading}
          hidePasswordControls={ssoGuard.ssoDetected}
          onEmailOnlySubmit={ssoGuard.canUseEmail}
        />

        {captcha.field}
      </div>
    </>
  );
}
